import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../components/Panel'
import { StatusTag } from '../../../../components/tableTemplates'
import { usePanelContext } from '../../../../contexts/panel'
import { useToastContext } from '../../../../contexts/toast'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../utils/helper/validateFormikFields'
import i18n from '../../../../utils/i18n'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import {
  OrderConfirmationDto,
  OrderConfirmationPatchDto,
} from '../../data/model'

interface Props {
  data?: OrderConfirmationDto
  onSubmit: (data: OrderConfirmationPatchDto) => Promise<void>
  orderResponseId: string
  isLoading: boolean
}

interface FormValues extends OrderConfirmationDto {}

type FormFieldName = keyof OrderConfirmationDto

export const OrderConfirmation: React.FC<Props> = ({
  data,
  onSubmit,
  orderResponseId,
  isLoading,
}) => {
  const [isEnableErrorMsg, setIsEnableErrorMsg] = useState<boolean>(false)
  const { t } = useTranslation()
  const { language } = i18n

  const handleSubmit =
    onSubmit ||
    (async () => {
      return
    })
  const panelId = 'order-confirmation'
  const { isEdit } = usePanelContext()

  const { showToast } = useToastContext()
  const { uiConfig: config } = useConfigContext()

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: { ...data },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationId',
          config: config.orderResponse.orderConfirmation.confirmationId,
        },
        {
          fieldName: 'department',
          config: config.orderResponse.orderConfirmation.department,
        },
        {
          fieldName: 'name',
          config: config.orderResponse.orderConfirmation.name,
        },
        {
          fieldName: 'phone',
          config: config.orderResponse.orderConfirmation.phone,
        },
        {
          fieldName: 'email',
          config: config.orderResponse.orderConfirmation.email,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: data => {
      try {
        // send data in OrderData.tsx
        handleSubmit(data)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'orderResponse.orderConfirmation.save.messages.success.summary',
          ),
          detail: t(
            'orderResponse.orderConfirmation.save.messages.success.detail',
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'orderResponse.orderConfirmation.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      }
    },
  })

  const showError = (name: FormFieldName) => {
    const hasEmailError = formik.errors.email !== undefined
    const hasConfirmationIdError = formik.errors.confirmationId !== undefined

    return hasEmailError ||
      hasConfirmationIdError ||
      (hasEmailError && hasConfirmationIdError)
      ? !!formik.errors[name]
      : !!(formik.touched[name] && formik.errors[name])
  }

  const isFormFieldValid = (name: FormFieldName) => showError(name)
  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  useEffect(() => {
    if (
      formik.errors.confirmationId !== undefined ||
      formik.errors.email !== undefined
    ) {
      setIsEnableErrorMsg(true)
    } else {
      setIsEnableErrorMsg(false)
    }
  }, [isEnableErrorMsg, formik.errors.confirmationId, formik.errors.email])

  const movedComponentIfErrorMsgEnable = (msgIdText: any) => {
    return isEnableErrorMsg ? (
      <div style={{ minHeight: '2.5em' }}>{getFormErrorMessage(msgIdText)}</div>
    ) : (
      <div>{getFormErrorMessage(msgIdText)}</div>
    )
  }

  // ************************************************************
  // components
  // ************************************************************
  // responsePeriod
  const responsePeriod: Date[] = [
    new Date(Date.parse(data?.responsePeriod?.Start!)), // start date
    new Date(Date.parse(data?.responsePeriod?.End!)), // end date
  ]

  const responsePeriodLabel = (
    <label className="text-sm" htmlFor="responsePeriod"></label>
  )

  const responsePeriodComponent = (
    <Calendar
      id="responsePeriod"
      value={responsePeriod}
      selectionMode="range"
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )
  const responsePeriodErrorMsg =
    movedComponentIfErrorMsgEnable('responsePeriod')

  const responsePeriodWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={responsePeriodComponent}
      label={responsePeriodLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={responsePeriodErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationId
  const ConfirmationIdLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationId'),
      })}
      htmlFor="confirmationId"
    ></label>
  )

  const ConfirmationIdComponent = (
    <InputText
      id="confirmationId"
      value={formik.values.confirmationId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationId'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationIdErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationId')

  const ConfirmationIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={ConfirmationIdComponent}
      label={ConfirmationIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // response
  const responseLabel = (
    <label className="text-sm w-full" htmlFor="response"></label>
  )

  const responseComponent = (
    <StatusTag id="response" status={data?.confirmationCode} className="p-2" />
  )

  const responseErrorMsg = movedComponentIfErrorMsgEnable('response')

  const responseWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={responseComponent}
      label={responseLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={responseErrorMsg}
      isLoading={isLoading}
    />
  )

  // department
  const departmentLabel = (
    <label className="text-sm" htmlFor="department"></label>
  )

  const departmentComponent = (
    <InputText
      id="department"
      value={formik.values.department}
      onChange={formik.handleChange}
      className="w-full"
      disabled={!isEdit(panelId)}
    />
  )

  const departmentErrorMsg = movedComponentIfErrorMsgEnable('department')

  const departmentWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={departmentComponent}
      label={departmentLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={departmentErrorMsg}
      isLoading={isLoading}
    />
  )

  // name
  const nameLabel = <label className="text-sm" htmlFor="name"></label>

  const nameComponent = (
    <InputText
      id="name"
      value={formik.values.name}
      onChange={formik.handleChange}
      className="w-full"
      disabled={!isEdit(panelId)}
    />
  )

  const nameErrorMsg = movedComponentIfErrorMsgEnable('name')

  const nameWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={nameComponent}
      label={nameLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={nameErrorMsg}
      isLoading={isLoading}
    />
  )

  // phone
  const phoneLabel = <label className="text-sm" htmlFor="phone"></label>

  const phoneComponent = (
    <InputText
      id="phone"
      value={formik.values.phone}
      onChange={formik.handleChange}
      className="w-full"
      disabled={!isEdit(panelId)}
    />
  )

  const phoneErrorMsg = movedComponentIfErrorMsgEnable('phone')

  const phoneWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={phoneComponent}
      label={phoneLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={phoneErrorMsg}
      isLoading={isLoading}
    />
  )

  // email
  const emailLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('email'),
      })}
      htmlFor="email"
    ></label>
  )

  const emailComponent = (
    <InputText
      id="email"
      value={formik.values.email}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('email'),
      })}
      keyfilter="email"
      disabled={!isEdit(panelId)}
    />
  )

  const emailErrorMsg = movedComponentIfErrorMsgEnable('email')

  const emailWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={emailComponent}
      label={emailLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={emailErrorMsg}
      isLoading={isLoading}
    />
  )

  // remarks
  const remarksLabel = <label className="text-sm" htmlFor="remarks"></label>

  const remarksComponent = (
    <InputTextarea
      id="remarks"
      value={formik.values.remarks}
      onChange={formik.handleChange}
      rows={5}
      className="w-full"
      disabled={!isEdit(panelId)}
    />
  )

  const remarksErrorMsg = movedComponentIfErrorMsgEnable('remarks')

  const remarksWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.orderConfirmation}
      component={remarksComponent}
      label={remarksLabel}
      className="col-12 md:col-12 lg:col-12 xl:col-9"
      errorMsg={remarksErrorMsg}
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      formik={formik}
      header={getConfigTranslation(
        config.orderResponse.orderConfirmation.title,
      )}
      id={panelId}
      visible={config.orderResponse.orderConfirmation.enabled}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="formgrid grid">
          {/* responsePeriod */}
          {responsePeriodWrapper}

          {/* confirmationId */}
          {ConfirmationIdWrapper}

          {/* response */}
          {responseWrapper}

          {/* department */}
          {departmentWrapper}

          {/* name */}
          {nameWrapper}

          {/* phone */}
          {phoneWrapper}

          {/* email */}
          {emailWrapper}

          {/* remarks */}
          {remarksWrapper}
        </div>
      </form>
    </PanelContainer>
  )
}
